import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { ConnectionAuthKind } from 'shell-models-lib';
import { format } from 'app/shared/utils/string.util';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { optionsOffOn } from './linked-service-shared-fields';
import { DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';

const parameterizeControl = (commonControl: ControlMetadata): ControlMetadata => {
    // only process the tree nodes, should not embedded the child control inside the commonControl
    if (commonControl.type === 'textbox') {
        return { type: 'parameterizable', child: commonControl };
    } else if (commonControl.type === 'dynamic-akv-switcher') {
        const { bind, child } = commonControl;
        const paramlizedControl: ControlMetadata = {
            enableAKVParameterization: true,
            ...commonControl,
        };
        paramlizedControl.child = {
            type: 'parameterizable',
            attribute: bind,
            disableAddParameter: true,
            child: { ...child }
        };
        return paramlizedControl;
    }
    return commonControl;
};

const applicationServerControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'server',
        label: stringResources.ModelName_ServerName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'systemNumber',
        label: stringResources.ModelName_SystemNumber,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    }
];

const groupControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'messageServer',
        label: stringResources.ModelName_MessageServer,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'messageServerService',
        label: stringResources.ModelName_MessageServerService,
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'systemId',
        label: stringResources.ModelName_SystemId,
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'logonGroup',
        label: stringResources.ModelName_LogonGroup,
        isLabelVertical: true
    }
];

const logonTypeCommonControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'clientId',
        label: stringResources.ModelName_ClientId,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'language',
        label: stringResources.Language,
        isLabelVertical: true
    }
];

const sncModeInputControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'sncMyName',
        label: stringResources.ModelName_SncMyName,
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'sncPartnerName',
        label: stringResources.ModelName_SncPartnerName,
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'sncLibraryPath',
        label: stringResources.ModelName_SncLibraryPath,
        isLabelVertical: true
    },
];

const sncModeSelectControl: ControlMetadata = {
    type: 'select',
    bind: 'sncQop',
    label: stringResources.ModelName_SncQop,
    options: [
        {
            label: stringResources.None,
            value: undefined
        },
        {
            label: stringResources.Authentication,
            value: '1'
        },
        {
            label: stringResources.SapTable_SncQop_Integrity,
            value: '2'
        },
        {
            label: stringResources.SapTable_SncQop_Privacy,
            value: '3'
        },
        {
            label: stringResources.Default,
            value: '5'
        },
        {
            label: stringResources.MaximumLabel,
            value: '8'
        }
    ],
    isLabelVertical: true
};

const sncModeCommonControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'userName',
        label: stringResources.ModelName_UserName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'password',
        label: stringResources.ModelName_Password,
        child: {
            type: 'password',
            label: stringResources.ModelName_Password,
            required: true,
            isLabelVertical: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.SapTable_DisplayText),
            stringResources.ModelName_Password)
    }
];

export const sapTableMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.sapTable.linkedServiceType,
    displayName: stringResources.SapTable_DisplayText,
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-SapTable'
    },
    isOnPremises: true,
    supportDatasetV2: true,
    isFirstClass: true,
    supportedInTrident: GlobalVariables.enableOPDG,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'SAPTableMessageServer',
    },
    supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
    supportOPDG: GlobalVariables.enableOPDG,
    supportCopyJobSource: GlobalVariables.enableOPDG,
    supportedJobModesAsSource: ['Batch'],
    supportMultipleIncrementalColumns: GlobalVariables.copyJobMultipleIncrementalColumnsForSAPTable,
    supportAutoFillCopilotInput: GlobalVariables.enableOPDG,
    isKnownInPipelineContext: GlobalVariables.enableOPDG,
    supportFirstClassParameterExperience: true,
    copySourceTypes: [adfModelTypeMap.sapTable.copySourceType],
    copySinkTypes: [],
    onetimeQueryTemplate: '',
    timedQueryTemplate: '',
    category: ['Database'],
    controls: [
        {
            type: 'radio-switch',
            bind: 'logonType',
            label: stringResources.SapTable_LogonType,
            isLabelVertical: true,
            nested: 1,
            options: [
                {
                    label: stringResources.SapTable_LogonType_ApplicationServer,
                    value: 'applicationServer'
                },
                {
                    label: stringResources.GroupLabel,
                    value: 'group'
                }
            ],
            defaultValue: 'applicationServer',
            cases: [
                {
                    caseValue: 'applicationServer',
                    children: [
                        ...applicationServerControls.map(parameterizeControl)
                    ]
                },
                {
                    caseValue: 'group',
                    children: [
                        ...groupControls.map(parameterizeControl)
                    ]
                }
            ]
        },
        ...logonTypeCommonControls.map(parameterizeControl),
        {
            type: 'radio-switch',
            nested: 1,
            bind: 'sncMode',
            label: stringResources.ModelName_SncMode,
            isLabelVertical: true,
            options: optionsOffOn,
            defaultValue: false,
            cases: [
                {
                    caseValue: true,
                    children: [
                        ...sncModeInputControls.map(parameterizeControl),
                        sncModeSelectControl
                    ]
                }
            ]
        },
        ...sncModeCommonControls.map(parameterizeControl)
    ]
};
